import { ChangeDetectionStrategy, Component, ElementRef } from '@angular/core';
import { ColumnState, IHeaderParams } from 'ag-grid-community';
import { combineLatestWith, Observable, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import * as locationListSelectors from '../../../location-list/state/location-list-selectors';
import { SortingEnum } from '../../../enums/sorting-enum';
import { map } from 'rxjs/operators';
import { DataSourceStatus } from '../../../enums/data-source-status';
import { getDataSourceIconName, getDataSourceIconTooltipKey } from '../../../data-source-utils';
import { TranslateService } from '@ngx-translate/core';
import { LocationListLocalStoreService } from '../../location-list-local-store.service';
import { getEnumValues } from '../../../../../core/utilities';
import { FieldGroup, getFieldIdsOfFieldGroup } from '../../../location-utils';
import { LocationListApiService } from '../../location-list-api.service';
import { DynamicColumnNoteComponent } from '../../dynamic-column-note/dynamic-column-note.component';
import { StaticColumnNoteComponent } from '../../static-column-note/static-column-note.component';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { ColumnSortingButtonComponent } from '../../../../global/column-sorting-button/column-sorting-button.component';
import { LetDirective } from '@ngrx/component';
import { AgHeaderComponent } from '../../../../global/gx-ag-grid/ag-header.component';

export interface DataColumnHeaderParams extends IHeaderParams {
  staticColumnNote?: string;
  hasDataSourceIcon: boolean;
}

@Component({
    selector: 'app-data-column-header',
    templateUrl: './data-column-header.component.html',
    styleUrls: ['../../../../global/gx-ag-grid/ag-basic-header/ag-basic-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        LetDirective,
        ColumnSortingButtonComponent,
        MatIcon,
        NgClass,
        MatTooltip,
        StaticColumnNoteComponent,
        DynamicColumnNoteComponent,
    ],
})
export class DataColumnHeaderComponent extends AgHeaderComponent<DataColumnHeaderParams> {
  private dataSourceIconType$ = this.params$.pipe(switchMap((params) =>
      this.store.select(locationListSelectors.selectDataSourceIconType(params?.column?.getId()))));

  public viewModel$: Observable<{
    params: DataColumnHeaderParams,
    sortDirection: SortingEnum,
    sortIndex: number,
    dataSourceIconType: Lowercase<DataSourceStatus>,
    dataSourceTooltip: string,
    dataSourceIconName: string,
    isToggleable: boolean,
  }> = this.params$.pipe(
      combineLatestWith(this.dataSourceIconType$,
          this.locationListLocalStore.columnStateDictionary$,
          this.locationListLocalStore.columnStateArray$),
      map(([params,
             dataSourceIconType,
             columnStateDictionary,
             columnState]) => {
        const fieldGrps = getEnumValues(FieldGroup);
        const colIds = columnState.map(columnState => columnState.colId);
        let toggleableColumnIds: string[] = [];

        fieldGrps.forEach(fieldGrp => toggleableColumnIds =
            toggleableColumnIds.concat(getFieldIdsOfFieldGroup(fieldGrp, colIds)));

        return {
          params,
          sortDirection: columnStateDictionary[params?.column?.getId()]?.sort ?
              columnStateDictionary[params.column.getId()].sort === 'asc' ?
                  SortingEnum.ASCENDING :
                  SortingEnum.DESCENDING :
              SortingEnum.NONE,
          sortIndex: columnStateDictionary[params?.column?.getId()]?.sortIndex,
          dataSourceIconType,
          dataSourceTooltip: params?.hasDataSourceIcon ? this.translate.instant(getDataSourceIconTooltipKey(dataSourceIconType)) : '',
          dataSourceIconName: params?.hasDataSourceIcon ? getDataSourceIconName(dataSourceIconType) : '',
          isToggleable: toggleableColumnIds.includes(params.column.getId())
        }
      }),
  );

  constructor(elementRef: ElementRef,
              private store: Store,
              private locationListLocalStore: LocationListLocalStoreService,
              private locationListApi: LocationListApiService,
              private translate: TranslateService) {
    super(elementRef)
  }

  /**
   * Hides column
   * @param params
   */
  onHideClick(params: DataColumnHeaderParams) {
    const columnState: ColumnState[] = [
      {
        colId: params.column.getColId(),
        hide: true
      }
    ];
    this.locationListApi.setColumnState(columnState, false);
  }
}
